import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Image } from 'components';
import { useApi } from 'contexts/Api/Api';
import { useContent } from 'contexts/Content/Content';
import { usePage } from 'contexts/Page/Page';
import { getUrl } from 'helpers';
import { useMemo } from 'react';

export default function Landing() {
    const api = useApi();
    const content = useContent();
    const page = usePage();

    const buttonDisabled = useMemo(() => api.authenticate && api.loadingAuthState, [api.authenticate, api.loadingAuthState]);

    function handlePlay() {
        if (api.authenticate && !api.authenticated) {
            api.showAuthModal('login');
            return;
        }

        page.show(api.getResumeLesson('1.1'));
    }

    const points = [
        content.landing.point1,
        content.landing.point2,
        content.landing.point3,
        content.landing.point4,
        content.landing.point5,
    ];

    return (
        <Box
            sx={{
                alignItems: 'stretch',
                backgroundImage: `url('${getUrl(content.landing.background)}')`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                color: '#FFF',
                display: 'flex',
                flex: 1,
                flexDirection: { xs: 'column', sm: 'row' },
            }}
        >
            <Box
                sx={{
                    alignSelf: 'center',
                    flex: 1,
                    padding: 10,
                    textAlign: 'center'
                }}>
                <Image alt={content.landing.illustrationAlt} src={content.landing.illustration} maxWidth={500} />
            </Box>
            <Box
                sx={{
                    bgcolor: 'rgba(30, 30, 30, 0.4)',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 3,
                    maxWidth: 570,
                    padding: 4
                }}>
                <Box sx={{
                    display: { sm: 'none' }
                }}>
                    <Typography variant="montserrat616" sx={{
                        textTransform: 'uppercase'
                    }}>
                        {content.landing.tagline}
                    </Typography>
                    <Typography variant="montserrat828" sx={{
                        marginBottom: 0
                    }}>
                        {content.landing.title}
                    </Typography>
                    <Typography variant="montserrat616" sx={{
                        marginBottom: 0
                    }}>
                        {content.landing.subtitle}
                    </Typography>
                </Box>

                <Box sx={{
                    display: { xs: 'none', sm: 'block' }
                }}>
                    <Typography variant="montserrat718" sx={{
                        textTransform: 'uppercase'
                    }}>
                        {content.landing.tagline}
                    </Typography>
                    <Typography variant="montserrat836" sx={{
                        marginBottom: 0
                    }}>
                        {content.landing.title}
                    </Typography>
                    <Typography variant="montserrat718" sx={{
                        marginBottom: 0
                    }}>
                        {content.landing.subtitle}
                    </Typography>
                </Box>

                <Typography
                    dangerouslySetInnerHTML={{ __html: content.landing.text }}
                    variant="montserrat518"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3
                    }} />

                <Box component="table">
                    <tbody>
                        {points.map((point, index) => (
                            <Box component="tr" key={index}>
                                <Box
                                    component="td"
                                    sx={{
                                        paddingBottom: 1,
                                        width: 48
                                    }}>
                                    <Image alt={point.iconAlt} src={point.icon} />
                                </Box>
                                <Box
                                    component="td"
                                    sx={{
                                        paddingBottom: 1,
                                        paddingLeft: 1
                                    }}>
                                    <Typography variant="montserrat518">{point.label}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </tbody>
                </Box>

                <Button
                    aria-label={content.landing.button}
                    data-testid="landing-play-button"
                    disabled={buttonDisabled}
                    size="large"
                    startIcon={buttonDisabled ? <CircularProgress aria-hidden color="inherit" size="18px" /> : null}
                    sx={{ textTransform: 'uppercase', width: { xs: '100%', sm: 'auto' } }}
                    onClick={handlePlay}
                >
                    {content.landing.button}
                </Button>
            </Box>
        </Box>
    );
}
